/**
 * Suggested-resources API client (Phase 1.7).
 *
 * Wraps the 6 HTTP routes under /v1/suggested-resources/:
 *   submit / list / count / get / approve / reject / withdraw
 *
 * Mirror of src/lib/suggestedEdits.ts shape for client consistency.
 *
 * Cache invalidation policy: every mutation that changes the
 * suggested_resources status (submit + approve/reject/withdraw)
 * invalidates BOTH `['admin-review', 'badge-count']` AND
 * `['suggested-resources']` keys; approve additionally invalidates
 * `['providers', ...]` keys (since INSERT into provider_organizations
 * happens). Callers wire via TanStack Query's onSuccess hooks.
 */

import { writeFetch } from './writeFetch';
import type {
  SuggestedResource,
  SuggestedResourcesCountResponse,
  SuggestedResourcesListResponse,
  SuggestedResourceStatus,
  SuggestedResourceTargetType,
} from './types';
import { apiErrorFrom } from './api';
import type { PostalParts } from '@/lib/address';

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function jsonRequest<T>(
  slug: string,
  path: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
    ...init,
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${path}`);
  return (await r.json()) as T;
}

// =====================================================================
// Payload shapes (mirror of api/_shared/repos/suggestedResources.ts)
// =====================================================================

// Exported so consumers (ResourceProposalReviewPane) can type-cast
// final_payload from form output to the submit shape.
export interface OrganizationSubmitPayload {
  name: string;
  category_slug: string;
  // Phase 1.7.1: kind dropped from the user-facing form; client may
  // still pass it (defaults to 'other' at the form layer).
  kind: string;
  subcategories?: string[];
  specialties?: string[];
  counties?: string[];
  phone?: string | null;
  /** The address as its five parts (a legacy proposal may hold text). */
  address?: PostalParts | string | null;
  website?: string | null;
  email?: string | null;
  notes?: string | null;
  medicaid?: string | null;
  dementia_unit?: boolean | null;
  phone_2?: string | null;
  fax?: string | null;
  payment_types?: string[] | null;
  service_delivery_model?: string | null;
  // Phase 1.7.1 — Contact-person picker (XOR pair). Exactly one of
  // contact_individual_id (existing) or contact_individual_inline
  // (inline-create payload). Server validator enforces.
  contact_individual_id?: string | null;
  contact_individual_inline?: IndividualSubmitPayload | null;
  /**
   * Phase 1.7.4 — multi-add contact entries. Each entry has exactly one
   * of `existing_id` / `inline`; server validator enforces per-entry
   * XOR. When present, supersedes the legacy singular fields. The
   * apply path creates one provider_individual_organizations row per
   * entry (role='Contact'; is_primary=true on the first entry only).
   */
  contacts?: ContactEntrySubmit[] | null;
}

export interface ContactEntrySubmit {
  existing_id?: string | null;
  inline?: IndividualSubmitPayload | null;
}

export interface IndividualSubmitPayload {
  full_name: string;
  title?: string | null;
  email?: string | null;
  /** @deprecated use business_phone explicitly; kept for back-compat. */
  phone?: string | null;
  // Phase 1.7.1 — explicit business_phone + cell_phone + address +
  // is_referral_partner (all map to existing columns on
  // provider_individuals; no schema additions needed).
  business_phone?: string | null;
  cell_phone?: string | null;
  /** A person's fax (0242), as an organization's. */
  fax?: string | null;
  /** The address as its five parts (a legacy proposal may hold text). */
  address?: PostalParts | string | null;
  is_referral_partner?: boolean | null;
  /**
   * Phase 1.7.4 — affiliation entries support inline-create org via
   * `inline` (in addition to the existing `provider_organization_id`
   * pointer to an already-created org). Per-entry XOR enforced server-
   * side. Pre-1.7.4 entries continue to apply unchanged.
   */
  affiliations?: Array<{
    provider_organization_id?: string | null;
    inline?: OrganizationSubmitPayload | null;
    role?: string | null;
    title?: string | null;
    is_primary?: boolean;
  }>;
  specialties?: string | null;
  notes?: string | null;
}

// =====================================================================
// suggestedResourcesApi
// =====================================================================

export const suggestedResourcesApi = {
  /** Submit a new-resource proposal. Any WRITE_ROLES user. */
  submit(
    slug: string,
    body: {
      target_type: SuggestedResourceTargetType;
      payload: OrganizationSubmitPayload | IndividualSubmitPayload;
    },
    signal?: AbortSignal,
  ): Promise<SuggestedResource> {
    return jsonRequest(
      slug,
      `/suggested-resources`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      signal,
    );
  },

  /**
   * The Directory's add in one step (direct_write): the form applied at
   * once, recorded approved with the caller as suggester and reviewer;
   * applied_target_id is the new listing.
   */
  addDirect(
    slug: string,
    body: {
      target_type: SuggestedResourceTargetType;
      payload: OrganizationSubmitPayload | IndividualSubmitPayload;
    },
    signal?: AbortSignal,
  ): Promise<SuggestedResource> {
    return jsonRequest(
      slug,
      `/suggested-resources/direct`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      signal,
    );
  },

  /**
   * List suggested resources. Server scopes non-stewards to own;
   * stewards see all.
   */
  list(
    slug: string,
    params: {
      status?: SuggestedResourceStatus;
      target_type?: SuggestedResourceTargetType;
      suggested_by?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<SuggestedResourcesListResponse> {
    const sp = new URLSearchParams();
    if (params.status) sp.set('status', params.status);
    if (params.target_type) sp.set('target_type', params.target_type);
    if (params.suggested_by) sp.set('suggested_by', params.suggested_by);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.offset !== undefined) sp.set('offset', String(params.offset));
    const qs = sp.toString();
    return jsonRequest(
      slug,
      `/suggested-resources${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      signal,
    );
  },

  /** Count pending suggested resources for the caller (badge consumer). */
  count(
    slug: string,
    params: { status?: SuggestedResourceStatus } = {},
    signal?: AbortSignal,
  ): Promise<SuggestedResourcesCountResponse> {
    const sp = new URLSearchParams();
    if (params.status) sp.set('status', params.status);
    const qs = sp.toString();
    return jsonRequest(
      slug,
      `/suggested-resources/count${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      signal,
    );
  },

  /** Get a single suggested resource by id. */
  get(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<SuggestedResource> {
    return jsonRequest(
      slug,
      `/suggested-resources/${encodeURIComponent(id)}`,
      { method: 'GET' },
      signal,
    );
  },

  /**
   * Approve — steward action. Transactionally INSERTs into the target
   * table (provider_organizations or provider_individuals) + M:N rows
   * and flips status to 'approved' with applied_target_id populated.
   */
  approve(
    slug: string,
    id: string,
    body: {
      reviewer_note: string | null;
      /**
       * Phase 1.8 — steward's edited payload (Q1 edit-and-approve).
       * NULL or omitted = approved-as-submitted.
       */
      final_payload?: OrganizationSubmitPayload | IndividualSubmitPayload | null;
      /**
       * Phase 1.8 — steward's type-switch result (Q2 warn-and-clear).
       * NULL or omitted = type unchanged.
       */
      final_target_type?: SuggestedResourceTargetType | null;
    } = { reviewer_note: null },
    signal?: AbortSignal,
  ): Promise<SuggestedResource> {
    return jsonRequest(
      slug,
      `/suggested-resources/${encodeURIComponent(id)}/approve`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      signal,
    );
  },

  /** Reject — steward action. Status flip + reviewer_note. */
  reject(
    slug: string,
    id: string,
    body: { reviewer_note: string | null } = { reviewer_note: null },
    signal?: AbortSignal,
  ): Promise<SuggestedResource> {
    return jsonRequest(
      slug,
      `/suggested-resources/${encodeURIComponent(id)}/reject`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      signal,
    );
  },

  /** Withdraw — suggester-only. */
  withdraw(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<SuggestedResource> {
    return jsonRequest(
      slug,
      `/suggested-resources/${encodeURIComponent(id)}/withdraw`,
      { method: 'POST' },
      signal,
    );
  },
};
